import { useEffect, useCallback, type RefObject } from 'react';
import { cn } from '@/utils/cn';
import { hexToRgba } from '@/utils/colors';
import type { Point } from '@/types/canvas';
import type { Annotation } from './RegionAnnotator';
import type { AxisConfig, TemplateAnchor, JointAngleConfig } from '@/types/api';
import type { PoseData } from '@/utils/landmarkDetection';
import { getLandmarkIndex } from '@/utils/landmarkDetection';
import { reconstructAnchorFromPose } from '@/utils/barycentric';
import { LANDMARK_NAME_TO_INDEX } from '@/utils/goalOverlayHelpers';
import { resolveFunctionalLandmarkName } from './functionalLandmarkResolution';

export interface AxisAnchorsState {
  start: TemplateAnchor | null;
  end: TemplateAnchor | null;
}

interface AnnotationCanvasOverlayProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
  annotations: Annotation[];
  pendingAnnotation: Annotation | null;
  selectedAnnotationId?: string | null;
  editingAnnotationId?: string | null;
  drawingMode: 'rectangle' | 'circle' | 'line' | null;
  isDrawing: boolean;
  startPoint: Point | null;
  currentPoint: Point | null;
  selectedColor: string;
  onMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  onMouseMove: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  onMouseUp: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  videoWidth: number;
  videoHeight: number;
  currentFrame: number;
  isVideoReady: boolean;
  axisConfig?: AxisConfig;
  poseData?: PoseData | null;
  className?: string;
  /** When true, mouse clicks are routed to onAxisAnchorClick instead of drawing */
  axisAnchorMode?: boolean;
  /** Called with normalized [0-1] coordinates when the user clicks in anchor mode */
  onAxisAnchorClick?: (normalizedX: number, normalizedY: number) => void;
  /** First anchor point waiting for the second click (normalized coords) */
  pendingAnchorPoint?: { x: number; y: number } | null;
  /** Completed anchor pairs for rendering the live axis preview */
  axisAnchors?: AxisAnchorsState | null;
  /** Template lead side — needed to resolve functional landmark names (lead_*, trail_*) */
  templateHandedness?: 'left' | 'right' | 'ambidextrous';
  /**
   * Detected lead side from the backend (detect_lead_side_for_video() result), threaded
   * from CreateTemplate → RegionAnnotator → here.  When provided, it takes precedence over
   * templateHandedness for the axis-line and joint-angle previews (FE#4 fix).
   */
  detectedLeadSide?: 'left' | 'right';
  /** Active joint angle config — draws a three-node angle arc preview on the canvas */
  jointAngleConfig?: JointAngleConfig;
}

/**
 * AnnotationCanvasOverlay - Handles canvas drawing and mouse interactions for annotations
 *
 * This component is responsible for:
 * 1. Drawing the video frame on the canvas
 * 2. Rendering saved and pending annotations (rectangles, circles, lines)
 * 3. Drawing preview shapes while the user is drawing
 * 4. Handling mouse events for creating new annotations
 */
function AnnotationCanvasOverlay({
  canvasRef,
  videoRef,
  annotations,
  pendingAnnotation,
  selectedAnnotationId = null,
  editingAnnotationId = null,
  drawingMode,
  isDrawing,
  startPoint,
  currentPoint,
  selectedColor,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  videoWidth,
  videoHeight,
  currentFrame,
  isVideoReady,
  axisConfig,
  poseData,
  className,
  axisAnchorMode = false,
  onAxisAnchorClick,
  pendingAnchorPoint = null,
  axisAnchors = null,
  templateHandedness = 'right',
  detectedLeadSide,
  jointAngleConfig,
}: AnnotationCanvasOverlayProps) {

  // =====================================================
  // Canvas Drawing Effect
  // =====================================================

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, videoWidth, videoHeight);

    const video = videoRef.current;

    // Draw video frame
    if (video && video.readyState >= 2) {
      try {
        ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
      } catch {
        // Error loading video frame
        ctx.fillStyle = '#fee2e2';
        ctx.fillRect(0, 0, videoWidth, videoHeight);
        ctx.fillStyle = '#dc2626';
        ctx.font = '16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Error loading video', videoWidth / 2, videoHeight / 2);
      }
    } else if (video) {
      // Video loading
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, videoWidth, videoHeight);
      ctx.fillStyle = '#6b7280';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Loading video frame...', videoWidth / 2, videoHeight / 2);
    } else {
      // No video loaded
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, videoWidth, videoHeight);
      ctx.fillStyle = '#6b7280';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No video loaded', videoWidth / 2, videoHeight / 2);
    }

    // Draw all annotations (saved + pending)
    const allAnnotations = [...annotations];
    if (pendingAnnotation) {
      allAnnotations.push(pendingAnnotation);
    }

    allAnnotations.forEach((ann) => {
      // An annotation the backend materialiser built from the coach's spoken
      // instruction has no position yet (PR-3, owner decision D5) — there is
      // nothing to draw until the coach places it. Skipping is the whole
      // point: the old client invented a placeholder box so that every
      // suggestion drew SOMETHING, in a spot nobody chose.
      const coords = ann.coordinates;
      if (!coords) return;

      // While an annotation is open for editing it stays in the saved list
      // (audit item FE-9), and the pending copy is drawn for it. Skip the
      // saved one so the coach does not see the same shape twice.
      if (editingAnnotationId && ann !== pendingAnnotation && ann.id === editingAnnotationId) {
        return;
      }

      const isPending = ann === pendingAnnotation;
      ctx.strokeStyle = ann.color;
      ctx.fillStyle = hexToRgba(ann.color, 0.2);
      ctx.lineWidth = isPending ? 2 : 3;
      if (isPending) {
        ctx.setLineDash([5, 5]);
      }

      // Draw rectangle
      if (ann.type === 'rectangle' && coords.width && coords.height) {
        ctx.strokeRect(coords.x, coords.y, coords.width, coords.height);
        ctx.fillRect(coords.x, coords.y, coords.width, coords.height);
      }
      // Draw circle
      else if (ann.type === 'circle' && coords.radius) {
        ctx.beginPath();
        ctx.arc(coords.x, coords.y, coords.radius, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.fill();
      }
      // Draw line
      else if (ann.type === 'line' && coords.x2 !== undefined && coords.y2 !== undefined) {
        ctx.beginPath();
        ctx.moveTo(coords.x, coords.y);
        ctx.lineTo(coords.x2, coords.y2);
        ctx.stroke();

        // Draw endpoint dots
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(coords.x, coords.y, 8, 0, 2 * Math.PI);
        ctx.fillStyle = ann.color;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(coords.x2, coords.y2, 8, 0, 2 * Math.PI);
        ctx.fillStyle = ann.color;
        ctx.fill();
        ctx.stroke();
      }

      ctx.setLineDash([]);

      // Grab handles on the selected annotation, so the coach can see that it
      // can be moved and where to take hold of it (audit item FE-9). Drawn
      // last so they sit above the shape's own fill.
      if (selectedAnnotationId && ann.id === selectedAnnotationId && !isPending) {
        const handles: Array<[number, number]> = [];
        if (ann.type === 'rectangle' && coords.width !== undefined && coords.height !== undefined) {
          handles.push([coords.x + coords.width, coords.y + coords.height]);
        } else if (ann.type === 'circle' && coords.radius !== undefined) {
          handles.push([coords.x + coords.radius, coords.y]);
        } else if (ann.type === 'line' && coords.x2 !== undefined && coords.y2 !== undefined) {
          handles.push([coords.x, coords.y], [coords.x2, coords.y2]);
        }
        for (const [hx, hy] of handles) {
          ctx.beginPath();
          ctx.arc(hx, hy, 7, 0, 2 * Math.PI);
          ctx.fillStyle = '#ffffff';
          ctx.fill();
          ctx.strokeStyle = ann.color;
          ctx.lineWidth = 3;
          ctx.stroke();
        }
      }
    });

    // Draw preview shape while drawing
    if (isDrawing && startPoint && currentPoint && drawingMode) {
      ctx.strokeStyle = selectedColor;
      ctx.fillStyle = hexToRgba(selectedColor, 0.2);
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);

      if (drawingMode === 'rectangle') {
        const width = currentPoint.x - startPoint.x;
        const height = currentPoint.y - startPoint.y;
        ctx.strokeRect(startPoint.x, startPoint.y, width, height);
        ctx.fillRect(startPoint.x, startPoint.y, width, height);
      } else if (drawingMode === 'circle') {
        const radius = Math.sqrt(
          Math.pow(currentPoint.x - startPoint.x, 2) +
          Math.pow(currentPoint.y - startPoint.y, 2)
        );
        ctx.beginPath();
        ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.fill();
      } else if (drawingMode === 'line') {
        ctx.beginPath();
        ctx.moveTo(startPoint.x, startPoint.y);
        ctx.lineTo(currentPoint.x, currentPoint.y);
        ctx.stroke();

        // Draw endpoint dots
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(startPoint.x, startPoint.y, 8, 0, 2 * Math.PI);
        ctx.fillStyle = selectedColor;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(currentPoint.x, currentPoint.y, 8, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      }

      ctx.setLineDash([]);
    }

    // Draw anchor mode overlay (cyan dots + dashed line for custom axis)
    if (axisAnchorMode && poseData?.landmarks) {
      const ANCHOR_COLOR = '#06B6D4'; // Cyan

      // Draw pending first point
      if (pendingAnchorPoint && !axisAnchors?.start) {
        const px = pendingAnchorPoint.x * videoWidth;
        const py = pendingAnchorPoint.y * videoHeight;
        ctx.save();
        ctx.fillStyle = ANCHOR_COLOR;
        ctx.shadowColor = ANCHOR_COLOR;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(px, py, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Draw placed anchors
      const startPos = axisAnchors?.start
        ? reconstructAnchorFromPose(axisAnchors.start, poseData.landmarks, LANDMARK_NAME_TO_INDEX)
        : null;
      const endPos = axisAnchors?.end
        ? reconstructAnchorFromPose(axisAnchors.end, poseData.landmarks, LANDMARK_NAME_TO_INDEX)
        : null;

      if (startPos) {
        const sx = startPos[0] * videoWidth;
        const sy = startPos[1] * videoHeight;
        ctx.save();
        ctx.fillStyle = ANCHOR_COLOR;
        ctx.shadowColor = ANCHOR_COLOR;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(sx, sy, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      if (endPos) {
        const ex = endPos[0] * videoWidth;
        const ey = endPos[1] * videoHeight;
        ctx.save();
        ctx.fillStyle = ANCHOR_COLOR;
        ctx.shadowColor = ANCHOR_COLOR;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(ex, ey, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Draw dashed connector line between both anchors
      if (startPos && endPos) {
        const sx = startPos[0] * videoWidth;
        const sy = startPos[1] * videoHeight;
        const ex = endPos[0] * videoWidth;
        const ey = endPos[1] * videoHeight;
        ctx.save();
        ctx.strokeStyle = ANCHOR_COLOR;
        ctx.lineWidth = 2.5;
        ctx.globalAlpha = 0.85;
        ctx.setLineDash([8, 5]);
        ctx.shadowColor = ANCHOR_COLOR;
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, ey);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }
    }

    // Draw axis line preview if both landmarks are selected
    if (!axisAnchorMode && axisConfig?.landmark_start && axisConfig?.landmark_end && poseData?.landmarks) {
      try {
        // FE#4 fix: prefer detectedLeadSide (threaded from CreateTemplate via
        // RegionAnnotator) over templateHandedness.  templateHandedness is always
        // 'ambidextrous' in the template-creation flow, making the naive fallback
        // permanently wrong for left-lead templates.  detectedLeadSide carries
        // either the coach-declared template_lead_side or the backend's
        // detect_lead_side_for_video() result.
        const leadSide: 'left' | 'right' =
          detectedLeadSide ?? (templateHandedness === 'left' ? 'left' : 'right');

        // Resolve functional names (lead_hip, mid_shoulder, etc.) to positions
        const resolveToPosition = (name: string): { x: number; y: number; vis: number } | null => {
          const resolved = resolveFunctionalLandmarkName(name, leadSide);
          if (!resolved) return null;

          if (Array.isArray(resolved)) {
            // mid_* → average two landmarks
            const idx1 = getLandmarkIndex(resolved[0]);
            const idx2 = getLandmarkIndex(resolved[1]);
            if (idx1 === -1 || idx2 === -1) return null;
            const lm1 = poseData!.landmarks[idx1];
            const lm2 = poseData!.landmarks[idx2];
            if (!lm1 || !lm2) return null;
            return {
              x: (lm1.x + lm2.x) / 2,
              y: (lm1.y + lm2.y) / 2,
              vis: Math.min(lm1.visibility, lm2.visibility),
            };
          }

          // Single anatomical name
          const idx = getLandmarkIndex(resolved);
          if (idx === -1) return null;
          const lm = poseData!.landmarks[idx];
          if (!lm) return null;
          return { x: lm.x, y: lm.y, vis: lm.visibility };
        };

        const startPos = resolveToPosition(axisConfig.landmark_start);
        const endPos = resolveToPosition(axisConfig.landmark_end);

        if (startPos && endPos && startPos.vis > 0.5 && endPos.vis > 0.5) {
            // Convert normalized coordinates to canvas coordinates
            const startX = startPos.x * videoWidth;
            const startY = startPos.y * videoHeight;
            const endX = endPos.x * videoWidth;
            const endY = endPos.y * videoHeight;

            // Draw dashed amber line
            ctx.strokeStyle = '#f59e0b'; // Amber color
            ctx.lineWidth = 3;
            ctx.setLineDash([10, 5]);
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
            ctx.stroke();

            // Draw endpoint circles
            ctx.setLineDash([]);
            ctx.fillStyle = '#f59e0b';
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 2;

            ctx.beginPath();
            ctx.arc(startX, startY, 6, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(endX, endY, 6, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();

            // Calculate angle relative to the axis reference type
            const dx = endX - startX;
            const dy = endY - startY;
            let angleDeg: string;
            const refType = axisConfig.reference_type;
            if (refType === 'horizontal') {
              // Angle from horizontal: atan2(dy, dx), 0° = perfectly level
              const rad = Math.atan2(dy, dx);
              angleDeg = (rad * 180 / Math.PI).toFixed(1);
            } else {
              // Default: angle from vertical
              const rad = Math.atan2(dx, -dy); // Negative dy because canvas Y increases downward
              angleDeg = (rad * 180 / Math.PI).toFixed(1);
            }

            // Draw angle indicator
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;

            ctx.fillStyle = '#f59e0b';
            ctx.font = 'bold 14px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Draw background for text
            const text = `${angleDeg}°`;
            const textMetrics = ctx.measureText(text);
            const padding = 4;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.fillRect(
              midX - textMetrics.width / 2 - padding,
              midY - 10,
              textMetrics.width + padding * 2,
              20
            );

            // Draw text
            ctx.fillStyle = '#f59e0b';
            ctx.fillText(text, midX, midY);
        }
      } catch (error) {
        // Silently fail if landmarks not available
        console.debug('Could not draw axis preview:', error);
      }
    }

    // Draw joint angle preview: three landmark nodes + two connecting segments + angle arc
    if (jointAngleConfig?.enabled && jointAngleConfig.joint_name && poseData?.landmarks) {
      try {
        // Backend JOINT_DEFINITIONS (joint_angle_goal_evaluation.py:34-48)
        const JOINT_DEF: Record<string, [string, string, string]> = {
          left_elbow:    ['left_shoulder',  'left_elbow',   'left_wrist'],
          right_elbow:   ['right_shoulder', 'right_elbow',  'right_wrist'],
          left_shoulder: ['left_elbow',     'left_shoulder','left_hip'],
          right_shoulder:['right_elbow',    'right_shoulder','right_hip'],
          left_hip:      ['left_shoulder',  'left_hip',     'left_knee'],
          right_hip:     ['right_shoulder', 'right_hip',    'right_knee'],
          left_knee:     ['left_hip',       'left_knee',    'left_ankle'],
          right_knee:    ['right_hip',      'right_knee',   'right_ankle'],
          left_ankle:    ['left_knee',      'left_ankle',   'left_foot_index'],
          right_ankle:   ['right_knee',     'right_ankle',  'right_foot_index'],
        };

        // FE#4: same fix as axis preview — prefer detectedLeadSide over templateHandedness
        const leadSide: 'left' | 'right' =
          detectedLeadSide ?? (templateHandedness === 'left' ? 'left' : 'right');

        // Resolve functional joint name (lead_knee → left_knee or right_knee)
        let resolvedJointName = jointAngleConfig.joint_name;
        if (resolvedJointName.startsWith('lead_')) {
          resolvedJointName = (leadSide === 'left' ? 'left' : 'right') + resolvedJointName.slice(4);
        } else if (resolvedJointName.startsWith('trail_')) {
          const trailSide = leadSide === 'left' ? 'right' : 'left';
          resolvedJointName = trailSide + resolvedJointName.slice(5);
          // lead_ has 5 chars, trail_ has 6
          resolvedJointName = trailSide + '_' + jointAngleConfig.joint_name.slice(6);
        }

        const triplet = JOINT_DEF[resolvedJointName];
        if (!triplet) return; // Unknown joint name — skip

        const [nameA, nameV, nameB] = triplet; // A, Vertex, B

        const getLmPos = (name: string): { x: number; y: number; vis: number } | null => {
          const idx = getLandmarkIndex(name);
          if (idx === -1) return null;
          const lm = poseData!.landmarks[idx];
          if (!lm) return null;
          return { x: lm.x * videoWidth, y: lm.y * videoHeight, vis: lm.visibility };
        };

        const posA = getLmPos(nameA);
        const posV = getLmPos(nameV);
        const posB = getLmPos(nameB);

        if (!posA || !posV || !posB) return;
        if (posA.vis < 0.3 || posV.vis < 0.3 || posB.vis < 0.3) return;

        const { x: ax, y: ay } = posA;
        const { x: vx, y: vy } = posV;
        const { x: bx, y: by } = posB;

        // Compute angle at vertex (degrees)
        const dAVx = ax - vx; const dAVy = ay - vy;
        const dBVx = bx - vx; const dBVy = by - vy;
        const dotProduct = dAVx * dBVx + dAVy * dBVy;
        const magAV = Math.sqrt(dAVx ** 2 + dAVy ** 2) + 1e-10;
        const magBV = Math.sqrt(dBVx ** 2 + dBVy ** 2) + 1e-10;
        const cosAngle = Math.max(-1, Math.min(1, dotProduct / (magAV * magBV)));
        const angleDeg = Math.round(Math.acos(cosAngle) * 180 / Math.PI);

        const JOINT_COLOR = '#f59e0b'; // Amber — matches axis color scheme

        // Draw two segments from vertex to A and vertex to B
        ctx.save();
        ctx.strokeStyle = JOINT_COLOR;
        ctx.lineWidth = 2.5;
        ctx.setLineDash([8, 4]);

        ctx.beginPath();
        ctx.moveTo(vx, vy);
        ctx.lineTo(ax, ay);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(vx, vy);
        ctx.lineTo(bx, by);
        ctx.stroke();

        ctx.setLineDash([]);

        // Draw angle arc at vertex
        const arcRadius = Math.min(magAV, magBV, 40) * 0.35;
        const angleA = Math.atan2(dAVy, dAVx);
        const angleB = Math.atan2(dBVy, dBVx);

        ctx.strokeStyle = JOINT_COLOR;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(vx, vy, arcRadius, Math.min(angleA, angleB), Math.max(angleA, angleB));
        ctx.stroke();

        // Degree label near the arc
        const midAngle = (angleA + angleB) / 2;
        const labelR = arcRadius + 12;
        const labelX = vx + labelR * Math.cos(midAngle);
        const labelY = vy + labelR * Math.sin(midAngle);

        const labelText = `${angleDeg}°`;
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const tw = ctx.measureText(labelText).width;
        const pad = 3;
        ctx.fillStyle = 'rgba(0,0,0,0.65)';
        ctx.fillRect(labelX - tw / 2 - pad, labelY - 8, tw + pad * 2, 16);
        ctx.fillStyle = JOINT_COLOR;
        ctx.fillText(labelText, labelX, labelY);

        // Draw three landmark dots (A, vertex, B)
        const drawDot = (x: number, y: number, primary: boolean) => {
          ctx.beginPath();
          ctx.arc(x, y, primary ? 7 : 5, 0, 2 * Math.PI);
          ctx.fillStyle = primary ? JOINT_COLOR : 'rgba(245,158,11,0.5)';
          ctx.fill();
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        };

        drawDot(ax, ay, false);
        drawDot(vx, vy, true); // vertex is the key landmark
        drawDot(bx, by, false);

        // Goal name label near the vertex
        const goalLabel = jointAngleConfig.joint_name
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        ctx.font = '11px sans-serif';
        ctx.fillStyle = JOINT_COLOR;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(goalLabel, vx, vy - 10);

        ctx.restore();
      } catch (error) {
        console.debug('Could not draw joint angle preview:', error);
      }
    }
  }, [
    annotations,
    pendingAnnotation,
    selectedAnnotationId,
    editingAnnotationId,
    isDrawing,
    startPoint,
    currentPoint,
    drawingMode,
    selectedColor,
    videoWidth,
    videoHeight,
    currentFrame,
    isVideoReady,
    canvasRef,
    videoRef,
    axisConfig,
    poseData,
    axisAnchorMode,
    pendingAnchorPoint,
    axisAnchors,
    templateHandedness,
    detectedLeadSide,
    jointAngleConfig,
  ]);

  // Intercept clicks when in anchor mode
  const handleMouseDownWithAnchor = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (axisAnchorMode && onAxisAnchorClick) {
        const canvas = e.currentTarget;
        const rect = canvas.getBoundingClientRect();
        const normalizedX = (e.clientX - rect.left) / rect.width;
        const normalizedY = (e.clientY - rect.top) / rect.height;
        onAxisAnchorClick(normalizedX, normalizedY);
        return;
      }
      onMouseDown(e);
    },
    [axisAnchorMode, onAxisAnchorClick, onMouseDown],
  );

  return (
    <canvas
      ref={canvasRef}
      data-testid="annotation-canvas"
      width={videoWidth}
      height={videoHeight}
      onMouseDown={handleMouseDownWithAnchor}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      className={cn(
        'absolute top-0 left-0 w-full h-full',
        axisAnchorMode || drawingMode
          ? 'cursor-crosshair'
          : selectedAnnotationId
          // Something is selected and can be dragged, so say so with the
          // pointer as well as in words (audit item FE-9).
          ? 'cursor-move'
          : 'cursor-default',
        className
      )}
      style={{ aspectRatio: `${videoWidth}/${videoHeight}` }}
    />
  );
}

export default AnnotationCanvasOverlay;
